// resources/js/src/components/community/components/profile/IntroSection.tsx
import { Icon } from "@iconify/react";
import { IntroSectionProps } from "../interface/CommunityProfile";

export default function IntroSection({
  bio,
  work,
  education,
  location,
  joinedDate,
}: IntroSectionProps) {
  const hasDetails = work || education || location || joinedDate;
  const date = joinedDate ? new Date(joinedDate) : null;
  const justDate = date
    ? date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="bg-white dark:bg-deepSlate rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 transition-shadow duration-300 hover:shadow-md">
      {/* ===== Header ===== */}
      <div className="flex items-center gap-2 mb-4">
        <Icon
          icon="mdi:information-outline"
          className="text-primary-600 dark:text-primary-400 text-xl"
        />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          Intro
        </h2>
      </div>

      {/* ===== Bio ===== */}
      {bio && (
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
          {bio}
        </p>
      )}

      {/* ===== Details ===== */}
      {hasDetails ? (
        <div className="space-y-3">
          {work && (
            <DetailItem
              icon="mdi:briefcase-outline"
              text={
                <>
                  Works at{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {work}
                  </span>
                </>
              }
            />
          )}

          {education && (
            <DetailItem
              icon="mdi:school-outline"
              text={
                <>
                  Studied at{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {education}
                  </span>
                </>
              }
            />
          )}

          {location && (
            <DetailItem
              icon="mdi:map-marker-outline"
              text={
                <>
                  Lives in{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {location}
                  </span>
                </>
              }
            />
          )}

          {justDate && (
            <DetailItem
              icon="mdi:calendar-star-outline"
              text={
                <>
                  Joined{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {justDate}
                  </span>
                </>
              }
            />
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          No details to show yet.
        </p>
      )}

      {/* ===== Edit Button ===== */}
      <button className="w-full mt-5 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-all duration-200 active:scale-[0.97]">
        <Icon icon="mdi:pencil-outline" className="text-base" />
        Edit Details
      </button>
    </div>
  );
}

/* ============================================================
   Sub-component: DetailItem
   ============================================================ */
function DetailItem({
  icon,
  text,
}: {
  icon: string;
  text: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
      <Icon
        icon={icon}
        className="text-gray-400 dark:text-gray-500 text-xl shrink-0"
      />
      <span className="truncate">{text}</span>
    </div>
  );
}